import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Typography from "@mui/material/Typography";
import * as React from "react";
import { styled } from "@mui/material/styles";
import MuiAppBar from "@mui/material/AppBar";
import { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar/AppBar";
import { collapsedDrawerWidth, drawerWidth } from "../AppBarWithDrawer";
import LanguageSwitcher from "./LanguageSwitcher";
import { Avatar, Box, Chip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import OnboardingWizard from "../../pages/OnboardingWizard";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { useLocation } from "react-router-dom";
import NotificationsOffOutlinedIcon from "@mui/icons-material/NotificationsOffOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import { toggleOrderNotificationMute } from "../../redux/slices/OrderNotificationState";

interface Props {
  open: boolean;
  handleDrawerOpen: () => void;
}

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  marginLeft: open ? drawerWidth : collapsedDrawerWidth,
  width: `calc(100% - ${open ? drawerWidth : collapsedDrawerWidth}px)`,
  background: "rgba(13, 10, 11, 0.88)",
  borderBottom: "1px solid rgba(131, 60, 65, 0.18)",
  boxShadow: "none",
  color: "#f0ece8",
  backdropFilter: "blur(12px)",
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const routeMeta = [
  { match: "/app/dashboard", section: "Overview", title: "Dashboard" },
  { match: "/app/add-product", section: "Product", title: "New Product" },
  { match: "/app/drinks", section: "Product", title: "Drinks" },
  { match: "/app/food", section: "Product", title: "Food" },
  { match: "/app/combo", section: "Product", title: "Combo" },
  { match: "/app/order", section: "Operations", title: "Orders" },
  { match: "/app/campaign", section: "Growth", title: "Campaign" },
  { match: "/app/feedback", section: "Guest Experience", title: "Feedback" },
  { match: "/app/staff-management", section: "Team", title: "Staff Management" },
  { match: "/app/settings", section: "Configuration", title: "Settings" },
  {
    match: "/app/email-verification",
    section: "Account",
    title: "Email Verification",
  },
];

const getRouteMeta = (pathname: string) => {
  return (
    routeMeta.find((item) => pathname.startsWith(item.match)) || {
      section: "Admin",
      title: "Console",
    }
  );
};

export const TopBar = (props: Props) => {
  const { open, handleDrawerOpen } = props;
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.Auth.user);
  const { muted, unseenOrderIds } = useSelector(
    (state: RootState) => state.OrderNotification
  );
  const activeRoute = getRouteMeta(location.pathname);
  const initials = user.legalName
    ? user.legalName
        .split(" ")
        .map((name) => name.charAt(0))
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "MT";

  return (
    <>
      <AppBar position="fixed" open={open}>
        <Toolbar
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            minHeight: "64px !important",
            px: { xs: 2, md: 3 },
          }}
        >
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              mr: 2,
              border: "1px solid rgba(131, 60, 65, 0.24)",
              borderRadius: "8px",
              color: "#c8b8b0",
              backgroundColor: "rgba(31, 22, 21, 0.72)",
              "&:hover": {
                backgroundColor: "rgba(131, 60, 65, 0.16)",
                color: "#f9f3ef",
              },
              ...(open && { display: "none" }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, minWidth: 0 }}>
            <StorefrontIcon sx={{ color: "#9e4a50", fontSize: 18 }} />
            <Box sx={{ display: { xs: "none", sm: "block" }, minWidth: 0 }}>
              <Typography
                sx={{
                  color: "#7a6560",
                  fontFamily:
                    '"DM Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
                  fontSize: "9px",
                  letterSpacing: "0.14em",
                  lineHeight: 1.2,
                  textTransform: "uppercase",
                }}
              >
                {activeRoute.section}
              </Typography>
              <Typography
                sx={{
                  maxWidth: { sm: 180, lg: 280 },
                  overflow: "hidden",
                  color: "#f0ece8",
                  fontSize: "14px",
                  fontWeight: 600,
                  lineHeight: 1.35,
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {activeRoute.title}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: { xs: 1.2, sm: 2 },
              width: "100%",
            }}
          >
            <LanguageSwitcher />
            <IconButton
              onClick={() => dispatch(toggleOrderNotificationMute())}
              sx={{
                width: 34,
                height: 34,
                border: "1px solid rgba(131, 60, 65, 0.24)",
                borderRadius: "8px",
                color: muted ? "#7a6560" : unseenOrderIds.length ? "#d5a84c" : "#c8b8b0",
                bgcolor: unseenOrderIds.length
                  ? "rgba(213, 168, 76, 0.1)"
                  : "rgba(31, 22, 21, 0.72)",
                "&:hover": {
                  bgcolor: "rgba(131, 60, 65, 0.16)",
                  color: "#f9f3ef",
                },
              }}
              title={muted ? "Order sounds muted" : "Order sounds on"}
            >
              {muted ? (
                <NotificationsOffOutlinedIcon fontSize="small" />
              ) : (
                <NotificationsActiveOutlinedIcon fontSize="small" />
              )}
            </IconButton>
            <Chip
              label={user.membershipDetails.name || "Plan"}
              size="small"
              sx={{
                display: { xs: "none", sm: "inline-flex" },
                height: 26,
                border: "1px solid rgba(158, 74, 80, 0.28)",
                backgroundColor: "rgba(131, 60, 65, 0.12)",
                color: "#9e4a50",
                fontFamily:
                  '"DM Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
                fontSize: "10px",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  display: { xs: "none", md: "block" },
                  textAlign: "right",
                  minWidth: 0,
                }}
              >
                <Typography
                  className="dashboard-header mobile-dashboard"
                  sx={{
                    maxWidth: 220,
                    overflow: "hidden",
                    color: "#f0ece8",
                    fontSize: "13px",
                    fontWeight: 600,
                    lineHeight: 1.2,
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {user.legalName}
                </Typography>
                <Typography
                  sx={{
                    color: "#7a6560",
                    fontFamily:
                      '"DM Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
                    fontSize: "10px",
                    lineHeight: 1.4,
                  }}
                >
                  {user.username}
                </Typography>
              </Box>
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: "#833c41",
                  color: "#f9f3ef",
                  border: "1px solid rgba(249, 243, 239, 0.12)",
                  fontFamily:
                    '"DM Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
                  fontSize: "11px",
                  fontWeight: 600,
                }}
              >
                {initials}
              </Avatar>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      <OnboardingWizard />
    </>
  );
};
